import { Clock, Trash2 } from "lucide-react";
import type { CallWithExtras } from "../../../types/call";

import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";

import {
  formatCallTime,
  getDisplayIdFromCall,
} from "../../../utils/callDisplayUtils";
import { CallServiceIcon } from "../CallServiceIcon";

export interface CallListItemProps {
  call: CallWithExtras;
  selectCall: (id: string) => void;
  deleteCall: (id: string) => void;
}

export function CallListItem({
  call,
  selectCall,
  deleteCall,
}: Readonly<CallListItemProps>) {
  const isCalendarCall = !!call.originatingCalendarEventId;
  let title: string;

  if (isCalendarCall) {
    title = call.summary ?? getDisplayIdFromCall(call) ?? "Scheduled Meeting";
  } else {
    title = `Ad-hoc Call (${getDisplayIdFromCall(call)})`;
  }

  return (
    <TableRow key={call.id} className="h-14 group">
      <TableCell className="relative p-0 px-2">
        <button
          type="button"
          className="absolute inset-0 h-full w-full p-2 text-left transition-colors hover:bg-gray-100 hover:underline select-none"
          onClick={() => selectCall(call.id)}
          data-test={`call-row-${call.id}`}
        >
          <div className="flex items-center">
            <CallServiceIcon url={call.meetingUrl ?? call.url ?? ""} />
            <div className="ml-2 flex flex-col overflow-hidden">
              <span className="line-clamp-1 font-medium">{title}</span>
              <div className="flex items-center text-xs text-gray-500 select-none">
                <Clock className="mr-1 h-3 w-3" />
                {formatCallTime(call.startTime ?? call.createdAt ?? "")}
              </div>
            </div>
          </div>
        </button>
      </TableCell>
      {!call.originatingCalendarEventId ? (
        <TableCell className="w-14 p-2 text-right select-none">
          <Button
            variant="ghost"
            size="sm"
            className="invisible h-8 w-8 p-0 group-hover:visible"
            onClick={() => deleteCall(call.id)}
            disabled={["in_progress", "processed"].includes(call.status)}
            data-test={`delete-call-${call.id}`}
          >
            <Trash2 size={16} />
          </Button>
        </TableCell>
      ) : (
        <TableCell className="w-14 p-2 text-right select-none" />
      )}
    </TableRow>
  );
}
